import {
  WebGLRenderer,
  Scene,
  PerspectiveCamera,
  Color,
  Fog,
  Vector2,
  Vector3,
  ShaderMaterial,
  BackSide,
  SphereGeometry,
  Mesh,
  PMREMGenerator,
  HemisphereLight,
  DirectionalLight,
  PointLight,
  CircleGeometry,
  MeshBasicMaterial,
  MeshStandardMaterial,
  CylinderGeometry,
  BoxGeometry,
  Group,
  ExtrudeGeometry,
  ShapeGeometry,
  PlaneGeometry,
  RingGeometry,
  DoubleSide,
  CanvasTexture,
  RepeatWrapping,
  SRGBColorSpace,
  NoToneMapping,
  PCFSoftShadowMap,
  MathUtils,
  Sprite,
  SpriteMaterial,
  AdditiveBlending,
  Raycaster,
  type Texture,
} from 'three';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import {
  EffectComposer,
  EffectPass,
  RenderPass,
  BloomEffect,
  HueSaturationEffect,
  BrightnessContrastEffect,
  VignetteEffect,
  ToneMappingEffect,
  ToneMappingMode,
} from 'postprocessing';
import { makeBlobShape } from '../geometry/blob';
import { makeKokopelli, type KokoFrames } from '../character/kokopelli';
import { rng } from '../util/rng';
import type { Catalog } from '../data/catalog';
import type { AudioEngine } from '../audio/AudioEngine';
import { Particles, type ParticleMode } from '../particles/Particles';
import { FlyBys } from './FlyBys';

export type WeatherMode = 'sun' | 'storm' | 'wind' | 'night' | 'acid';

interface WeatherPreset {
  skyTop: number;
  skyBot: number;
  sunC: number;
  fog: [number, number, number]; // цвет, near, far
  hemi: number;
  dir: number;
  dirColor: number;
  bloom: number;
  saturation: number;
  contrast: number;
  particles: ParticleMode;
  wind: [number, number];
  lightning: boolean;
}

// Пресеты погоды — каждый перекрашивает небо/туман/свет/grade + частицы/ветер/молнию.
const WEATHER: Record<WeatherMode, WeatherPreset> = {
  sun: { skyTop: 0x1f5ccb, skyBot: 0xb2d2f3, sunC: 0xfff4d8, fog: [0xdce9f5, 60, 260], hemi: 0.5, dir: 2.8, dirColor: 0xfff1d6, bloom: 1.0, saturation: 0.34, contrast: 0.14, particles: 'dust', wind: [0.4, 0.2], lightning: false },
  storm: { skyTop: 0x2a3340, skyBot: 0x59647a, sunC: 0x9fb0c8, fog: [0x3a4150, 28, 150], hemi: 0.32, dir: 0.8, dirColor: 0xb8c4d8, bloom: 0.55, saturation: 0.06, contrast: 0.22, particles: 'rain', wind: [7, 3], lightning: true },
  wind: { skyTop: 0x2f6fc0, skyBot: 0xbfe0ef, sunC: 0xfff0d0, fog: [0xdfeaf2, 55, 240], hemi: 0.5, dir: 2.4, dirColor: 0xfff1d6, bloom: 0.95, saturation: 0.3, contrast: 0.15, particles: 'leaves', wind: [8, 2], lightning: false },
  night: { skyTop: 0x05103a, skyBot: 0x132a55, sunC: 0x9fb8e6, fog: [0x0c1838, 40, 200], hemi: 0.26, dir: 0.5, dirColor: 0x9fb8e6, bloom: 1.1, saturation: 0.18, contrast: 0.16, particles: 'dust', wind: [0.3, 0.2], lightning: false },
  acid: { skyTop: 0xff2d95, skyBot: 0x00e5ff, sunC: 0xffe93d, fog: [0x3a1050, 50, 240], hemi: 0.6, dir: 2.6, dirColor: 0xff7adf, bloom: 1.4, saturation: 0.95, contrast: 0.2, particles: 'sparks', wind: [1.2, 0.8], lightning: false },
};

interface Step {
  group: Group;
  coverMat: MeshStandardMaterial;
  borderMat: MeshStandardMaterial;
  baseEm: number;
  blobR: number;
  topLocalY: number;
  trackId: number;
  cs: number; // базовый размер обложки (для пульса в ритм)
  coverMaxScale: number; // макс. масштаб обложки = вписанный квадрат плиты (не вылезать за края)
}
interface Ripple {
  m: Mesh;
  mat: MeshStandardMaterial;
  active: boolean;
  t: number;
  dur: number;
  r0: number;
}
interface Cloud {
  s: Group; // кластер пуф-биллбордов (объёмное облако)
  sp: number;
}

export interface SceneState {
  absSlot: number; // абсолютный слот платформы, где стоит человечек
  trackId: number;
  isLive: boolean; // стоим на live-слоте (в эфире)
  liveSlot: number;
  liveStartMs: number; // начало live-трека (для синхрона аудио)
  durSec: number; // длительность трека текущей платформы
  playing: boolean;
}

const clamp = (v: number, a: number, b: number): number => Math.max(a, Math.min(b, v));

/**
 * 3D-сцена «реверс·радио» — порт 1:1 из концепта на наш стек: реальные обложки,
 * 60 слотов из Rust/WASM-ленты. Spine-ось по центру, blob-ступени на стеблях,
 * Кокопелли-маскот на текущей, ripple-кольца, орбита-камера, hop-навигация.
 */
export class RadioScene {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly camera: PerspectiveCamera;
  private composer: EffectComposer;

  private readonly N: number;
  private readonly rise = 0.95;
  private readonly radius = 7;
  private readonly angleStep = (Math.PI * 2) / 11;
  private readonly charH = 2.7;
  private readonly charFeet = 1.46;
  private readonly mob: boolean;
  private maxAniso: number;

  private steps: Step[] = [];
  private coverMeshes: Mesh[] = [];
  private conns: Mesh[] = [];
  private ripples: Ripple[] = [];
  private clouds: Cloud[] = [];
  private koko!: KokoFrames;
  private char!: Mesh;
  private blob!: Mesh;
  private sun = new Vector3();

  // погода
  private skyMat!: ShaderMaterial;
  private spineMat!: ShaderMaterial;
  private hemiLight!: HemisphereLight;
  private dirLight!: DirectionalLight;
  private lightningLight!: PointLight;
  private bloom!: BloomEffect;
  private hue!: HueSaturationEffect;
  private bc!: BrightnessContrastEffect;
  private particles!: Particles;
  private flybys!: FlyBys;
  private weatherMode: WeatherMode = 'sun';
  private weatherAuto = true;
  private _weatherTrackId = -1;
  private windX = 0;
  private windZ = 0;
  private _lightClock = 0;

  // навигация/состояние эфира
  private cur: number; // индекс ПЛАТФОРМЫ (0..N-1), где человечек; абс.слот = windowBase+cur
  private windowBase = 0; // абс.слот платформы 0
  private liveSlot = 0; // абс.слот, играющий в эфире сейчас
  private liveStartMs = 0;
  private following = true; // человечек следует за эфиром (стоит на live)
  private _liveClock = 0;
  private playing = true;
  private hop: { from: number; to: number; start: number; dur: number; height: number } | null = null;
  private pendingDir = 0;
  private _lastWheelStep = 0; // анти-инерция колеса: одна ступенька за жест
  private az = 0.7;
  private azTarget = 0.7;
  private polar = 1.32;
  private polarTarget = 1.32;
  private dist: number;
  private distTarget: number;
  private focus = new Vector3();

  // переиспользуемые вектора (цикл без аллокаций)
  private _t0 = new Vector3();
  private _t1 = new Vector3();
  private _cp = new Vector3();

  private raycaster = new Raycaster();
  private ndc = new Vector2();
  private _last = performance.now();
  private _lastInput = performance.now();
  private _spineY0 = 0; // нижняя Y ядра-стержня (для нормировки uStartY)
  private _spineH = 1; // высота ядра-стержня
  private _rippleClock = 0;
  private _rippleIdx = 0;
  private _frameKey = '';
  private _raf = 0;
  private stateCb: ((s: SceneState) => void) | null = null;

  constructor(
    private host: HTMLElement,
    private catalog: Catalog,
    private audio?: AudioEngine,
  ) {
    this.N = catalog.N;
    this.mob = (matchMedia('(pointer: coarse)').matches && innerWidth < 900) || innerWidth < 760;
    // live-слот эфира → окно спирали (live наверху, прошлое вниз)
    const live = catalog.slotAtTime(Date.now());
    this.liveSlot = live ? live.slotIndex : this.N - 1;
    this.liveStartMs = live ? live.startsAt : catalog.epochMs;
    this.windowBase = Math.max(0, this.liveSlot - (this.N - 1));
    this.cur = this.liveSlot - this.windowBase; // человечек на эфирной ступеньке
    this.dist = this.distTarget = this.mob ? 16 : 14.5;

    const W = host.clientWidth || innerWidth;
    const H = host.clientHeight || innerHeight;

    const renderer = new WebGLRenderer({ antialias: false, alpha: false, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(devicePixelRatio, this.mob ? 1.5 : 2));
    renderer.setSize(W, H); // updateStyle=true — Retina-safe
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = PCFSoftShadowMap;
    renderer.toneMapping = NoToneMapping; // тонмаппинг делает ToneMappingEffect в композере
    host.appendChild(renderer.domElement);
    this.renderer = renderer;
    this.maxAniso = renderer.capabilities.getMaxAnisotropy();

    this.scene.background = new Color(0x9fc4e8);
    this.scene.fog = new Fog(0xdce9f5, 60, 260);
    this.camera = new PerspectiveCamera(44, W / H, 0.3, 2400);

    const topY = (this.N - 1) * this.rise;
    this.buildSky();
    this.buildLights(topY);
    this.buildFloor();
    this.buildSpine(topY);
    this.buildSteps();
    this.buildRipples();
    this.buildCharacter();
    this.buildClouds(topY);

    // post
    const composer = new EffectComposer(renderer, { multisampling: this.mob ? 0 : 4 });
    composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloom = new BloomEffect({
      intensity: this.mob ? 0.8 : 1.0,
      luminanceThreshold: 0.5,
      luminanceSmoothing: 0.3,
      mipmapBlur: true,
      radius: 0.75,
    });
    this.hue = new HueSaturationEffect({ saturation: 0.32 }); // сочнее/ярче
    this.bc = new BrightnessContrastEffect({ brightness: 0.02, contrast: 0.14 });
    composer.addPass(
      new EffectPass(
        this.camera,
        this.bloom,
        new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC }),
        this.hue,
        this.bc,
        new VignetteEffect({ darkness: 0.5, offset: 0.32 }),
      ),
    );
    this.composer = composer;

    this.particles = new Particles(this.mob ? 800 : 1400);
    this.scene.add(this.particles.points);
    // сказочные пролёты (glTF, лениво) + иногда гигантская обложка из фонотеки
    this.flybys = new FlyBys(this.scene, topY - 10, this.mob, () => {
      const arr = this.catalog.coverTex;
      const idxs: number[] = [];
      for (let i = 0; i < arr.length; i++) if (arr[i]) idxs.push(i);
      return idxs.length ? arr[idxs[(Math.random() * idxs.length) | 0]] : null;
    });
    this.applyAutoWeather(); // стартовая погода по текущему треку

    this.stepTop(this.cur, this._cp);
    this.focus.set(this._cp.x, this._cp.y + 0.8, this._cp.z);

    // обложки приедут — обновим материалы соответствующих слотов
    catalog.onCover((trackId, tex) => {
      for (const s of this.steps) {
        if (s.trackId === trackId) {
          s.coverMat.map = tex;
          s.coverMat.needsUpdate = true;
        }
      }
    });

    this.bindInput();
    addEventListener('resize', this.onResize);
  }

  // ---------- sky + env ----------
  private buildSky(): void {
    this.sun.setFromSphericalCoords(1, MathUtils.degToRad(90 - 67), MathUtils.degToRad(205));
    this.skyMat = new ShaderMaterial({
      side: BackSide,
      depthWrite: false,
      fog: false,
      uniforms: {
        top: { value: new Color(0x1f5ccb) },
        bot: { value: new Color(0xb2d2f3) },
        sunC: { value: new Color(0xfff4d8) },
        sunDir: { value: this.sun.clone() },
      },
      vertexShader:
        'varying vec3 vP; void main(){ vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
      fragmentShader:
        'varying vec3 vP; uniform vec3 top; uniform vec3 bot; uniform vec3 sunC; uniform vec3 sunDir; void main(){ vec3 d = normalize(vP); float h = smoothstep(-0.18, 0.78, d.y); vec3 c = mix(bot, top, h); float s = max(dot(d, normalize(sunDir)),0.0); c = mix(c, sunC, pow(s,6.0)*0.32); c += sunC * pow(s,800.0) * 1.25; gl_FragColor = vec4(c,1.0); }',
    });
    this.scene.add(new Mesh(new SphereGeometry(800, 40, 24), this.skyMat));

    const pmrem = new PMREMGenerator(this.renderer);
    const prevFog = this.scene.fog;
    this.scene.fog = null;
    this.scene.environment = pmrem.fromScene(this.scene, 0.06).texture;
    this.scene.fog = prevFog;
  }

  private buildLights(topY: number): void {
    this.hemiLight = new HemisphereLight(0xbcd6f2, 0xe9e4d8, 0.42);
    this.scene.add(this.hemiLight);
    const dir = new DirectionalLight(0xfff1d6, 2.8);
    this.dirLight = dir;
    dir.position.copy(this.sun).multiplyScalar(120);
    dir.target.position.set(0, topY * 0.5, 0);
    dir.castShadow = true;
    dir.shadow.mapSize.set(this.mob ? 1024 : 2048, this.mob ? 1024 : 2048);
    dir.shadow.camera.near = 1;
    dir.shadow.camera.far = 170;
    dir.shadow.camera.left = -24;
    dir.shadow.camera.right = 24;
    dir.shadow.camera.top = topY * 0.5 + 26;
    dir.shadow.camera.bottom = -(topY * 0.5 + 26);
    dir.shadow.bias = -0.0004;
    dir.shadow.normalBias = 0.02;
    this.scene.add(dir, dir.target);
    const pPurple = new PointLight(0x685ae9, 60, 60, 2);
    pPurple.position.set(-12, topY * 0.4, 10);
    const pYellow = new PointLight(0xffcd07, 26, 50, 2);
    pYellow.position.set(12, topY * 0.7, -10);
    this.scene.add(pPurple, pYellow);
    // молния (для грозы) — вспышка по требованию
    this.lightningLight = new PointLight(0xcfe0ff, 0, 240, 1.4);
    this.lightningLight.position.set(0, topY * 0.7, 0);
    this.scene.add(this.lightningLight);
  }

  private buildFloor(): void {
    if (!this.mob) {
      const floor = new Reflector(new CircleGeometry(70, 64), {
        color: new Color(0x8b90a0),
        textureWidth: 1024,
        textureHeight: 1024,
        clipBias: 0.003,
      });
      floor.rotateX(-Math.PI / 2);
      floor.position.y = -1.6;
      this.scene.add(floor);
    }
    const plate = new Mesh(
      new CircleGeometry(70, 48),
      new MeshBasicMaterial({ color: 0xe6e9f1, transparent: true, opacity: this.mob ? 1 : 0.55 }),
    );
    plate.rotateX(-Math.PI / 2);
    plate.position.y = this.mob ? -1.6 : -1.61;
    this.scene.add(plate);
  }

  /** Тонкая процедурная карта нормалей (мягкий шум) — микро-рельеф материалов. */
  private surfaceNormalTex(): Texture {
    const S = 128;
    const cv = document.createElement('canvas');
    cv.width = cv.height = S;
    const ctx = cv.getContext('2d')!;
    const rnd = new Float32Array(S * S);
    for (let i = 0; i < S * S; i++) rnd[i] = Math.random();
    // box-blur (тороидальный) — сглаженный value-noise как высота
    const h = new Float32Array(S * S);
    const r = 3;
    for (let y = 0; y < S; y++)
      for (let x = 0; x < S; x++) {
        let s = 0;
        let n = 0;
        for (let dy = -r; dy <= r; dy++)
          for (let dx = -r; dx <= r; dx++) {
            s += rnd[((y + dy + S) % S) * S + ((x + dx + S) % S)];
            n++;
          }
        h[y * S + x] = s / n;
      }
    // высота → нормаль (Sobel по соседям), кодируем в RGB
    const img = ctx.createImageData(S, S);
    const strength = 1.6;
    for (let y = 0; y < S; y++)
      for (let x = 0; x < S; x++) {
        const l = h[y * S + ((x - 1 + S) % S)];
        const rr = h[y * S + ((x + 1) % S)];
        const u = h[((y - 1 + S) % S) * S + x];
        const d = h[((y + 1) % S) * S + x];
        const nx = (l - rr) * strength;
        const ny = (u - d) * strength;
        const nz = 1;
        const len = Math.hypot(nx, ny, nz) || 1;
        const idx = (y * S + x) * 4;
        img.data[idx] = ((nx / len) * 0.5 + 0.5) * 255;
        img.data[idx + 1] = ((ny / len) * 0.5 + 0.5) * 255;
        img.data[idx + 2] = ((nz / len) * 0.5 + 0.5) * 255;
        img.data[idx + 3] = 255;
      }
    ctx.putImageData(img, 0, 0);
    const tex = new CanvasTexture(cv);
    tex.wrapS = tex.wrapT = RepeatWrapping;
    tex.repeat.set(3, 3);
    return tex;
  }

  private buildSpine(topY: number): void {
    // Световое ядро-луч: вертикальные полосы бегут ВВЕРХ, мигают на бит и
    // растворяются у верхушки — «ритм возвышается по спирали». Тянется выше
    // верхней (live) ступени на extraTop, чтобы не обрываться на её границе.
    const extraTop = 22;
    const coreH = topY + 6 + extraTop;
    this.spineMat = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uBeat: { value: 0 },
        uBass: { value: 0 },
        uStartY: { value: 0.7 }, // нормированная высота играющей ступеньки (0..1)
        uColorA: { value: new Color(0x6a5cf0) },
        uColorB: { value: new Color(0xffcd07) },
      },
      vertexShader: /* glsl */ `
        varying float vY;
        void main(){ vY = uv.y; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
      `,
      fragmentShader: /* glsl */ `
        uniform float uTime; uniform float uBeat; uniform float uBass; uniform float uStartY;
        uniform vec3 uColorA; uniform vec3 uColorB;
        varying float vY;
        void main(){
          float local = vY - uStartY;            // 0 у играющей ступеньки, >0 выше
          if (local < 0.0) {                     // ниже ступеньки — спокойный тусклый глоу
            gl_FragColor = vec4(uColorA * 0.22, 0.16);
            return;
          }
          float band = 0.5 + 0.5 * sin(local * 28.0 - uTime * 3.0); // импульсы бегут ВВЕРХ от ступеньки
          band = pow(band, 2.0);
          float bright = 0.4 + uBass * 0.5 + band * (0.7 + uBeat * 4.5); // мигание на бит
          float fade = smoothstep(1.0, uStartY, vY); // ярко у ступеньки → растворяется к верхушке
          vec3 col = mix(uColorA, uColorB, clamp(uBeat * 0.7 + band * 0.12, 0.0, 1.0));
          gl_FragColor = vec4(col * bright * fade, fade);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      side: DoubleSide,
    });
    // глоу-импульсы СНАРУЖИ металла (r≈0.50 > shell 0.46) — свет бежит вверх по поверхности столба
    const core = new Mesh(new CylinderGeometry(0.5, 0.5, coreH, 24, 1, true), this.spineMat);
    core.position.y = topY / 2 + extraTop / 2; // низ остаётся ~−3, верх уходит к ~topY+25
    this._spineY0 = core.position.y - coreH / 2; // низ ядра (~−3)
    this._spineH = coreH;
    // столб — белый полированный металл, зеркальный (отражает scene.environment / небо)
    const shell = new Mesh(
      new CylinderGeometry(0.46, 0.46, topY + 6, 48),
      new MeshStandardMaterial({ color: 0xffffff, roughness: 0.04, metalness: 1.0, envMapIntensity: 1.6 }),
    );
    shell.position.y = topY / 2;
    shell.receiveShadow = true;
    this.scene.add(core, shell);
  }

  private buildSteps(): void {
    this.disposeSteps();
    // лёгкий PBR-полиш: микро-нормаль + отражение неба (PMREM) для «дороже» вида.
    // На mobile нормаль не вешаем (перф/память) — стиль воздушный сохраняется.
    const norm = this.mob ? null : this.surfaceNormalTex();
    const slabMat = new MeshStandardMaterial({
      color: 0xf7f8fc,
      roughness: 0.55,
      metalness: 0.12,
      envMapIntensity: 0.55,
      normalMap: norm,
      normalScale: new Vector2(0.32, 0.32),
    });
    const connMat = new MeshStandardMaterial({
      color: 0xccd1dc,
      roughness: 0.6,
      metalness: 0.2,
      envMapIntensity: 0.6,
      normalMap: norm,
      normalScale: new Vector2(0.4, 0.4),
    });
    const connGeo = new BoxGeometry(1, 0.14, 0.16); // несущая консоль ступени
    let durMin = Infinity;
    let durMax = -Infinity;
    for (let i = 0; i < this.N; i++) {
      const d = this.catalog.durSecAtSlot(this.windowBase + i);
      if (d < durMin) durMin = d;
      if (d > durMax) durMax = d;
    }
    const span = durMax - durMin || 1;

    for (let i = 0; i < this.N; i++) {
      const ang = i * this.angleStep;
      const gx = Math.cos(ang) * this.radius;
      const gz = Math.sin(ang) * this.radius;
      const gy = i * this.rise;
      const tN = (this.catalog.durSecAtSlot(this.windowBase + i) - durMin) / span;
      const blobR = 1.75 + tN * 1.4;
      const built = makeBlobShape(i * 31 + 5, blobR);
      const minR = built.minR;

      const group = new Group();
      group.position.set(gx, gy, gz);
      group.rotation.y = -ang;

      const ext = new ExtrudeGeometry(built.shape, {
        depth: 0.16,
        bevelEnabled: true,
        bevelThickness: 0.05,
        bevelSize: 0.06,
        bevelSegments: 1,
      });
      ext.rotateX(-Math.PI / 2);
      ext.computeVertexNormals();
      ext.computeBoundingBox();
      const topLocalY = ext.boundingBox!.max.y;
      const slab = new Mesh(ext, slabMat);
      slab.castShadow = true;
      slab.receiveShadow = true;
      group.add(slab);

      const haloGeo = new ShapeGeometry(makeBlobShape(i * 31 + 5, blobR * 1.09).shape);
      haloGeo.rotateX(-Math.PI / 2);
      const borderMat = new MeshStandardMaterial({
        color: 0x000000,
        emissive: 0x685ae9,
        emissiveIntensity: 1.4,
        roughness: 1,
        transparent: true,
        opacity: 0.92,
      });
      const halo = new Mesh(haloGeo, borderMat);
      halo.position.y = topLocalY - 0.02;
      group.add(halo);

      const cs = clamp(minR * 1.25, 1.2, 2.4);
      // вписанный квадрат: углы квадрата стороны s достают до minR при s=minR·√2;
      // 0.92 — запас на неровность блоба. Это потолок, за который обложка не растёт.
      const coverMaxScale = Math.max(1, (minR * Math.SQRT2 * 0.92) / cs);
      const trackId = this.catalog.trackIdAtSlot(this.windowBase + i);
      const coverMat = new MeshStandardMaterial({
        map: this.catalog.coverTex[trackId] || null,
        color: 0xe9ebf4,
        roughness: 0.42,
        metalness: 0.05,
      });
      const cover = new Mesh(new PlaneGeometry(cs, cs), coverMat);
      cover.rotation.x = -Math.PI / 2;
      cover.position.y = topLocalY + 0.012;
      cover.receiveShadow = true;
      cover.userData.index = i;
      group.add(cover);
      this.coverMeshes.push(cover);
      this.scene.add(group);

      // несущая консоль от стержня к плите (как у винтовой лестницы: ступень
      // крепится к центральному столбу). Внутренний конец заходит В стержень
      // (shell r≈0.42), внешний — под плиту; иначе ступень «висит в воздухе».
      const inner = 0.18; // внутрь оболочки стержня — стык без зазора
      const outer = this.radius - blobR * 0.32; // заходит под плиту
      const len = Math.max(0.8, outer - inner);
      const cR = inner + len / 2;
      const conn = new Mesh(connGeo, connMat);
      conn.scale.x = len;
      conn.position.set(Math.cos(ang) * cR, gy + topLocalY * 0.42, Math.sin(ang) * cR);
      conn.rotation.y = -ang;
      conn.castShadow = true;
      this.scene.add(conn);
      this.conns.push(conn);

      this.steps.push({ group, coverMat, borderMat, baseEm: 1.4, blobR, topLocalY, trackId, cs, coverMaxScale });
    }
  }

  /** Снять старые ступени (для пересборки окна при сдвиге эфира). */
  private disposeSteps(): void {
    for (const s of this.steps) {
      this.scene.remove(s.group);
      s.group.traverse((o) => {
        const m = o as Mesh;
        if (m.geometry) m.geometry.dispose();
      });
    }
    for (const c of this.conns) {
      this.scene.remove(c);
      c.geometry.dispose();
    }
    this.steps = [];
    this.coverMeshes = [];
    this.conns = [];
  }

  /** Сдвиг окна при продвижении эфира: live снова наверху. */
  private updateLive(dt: number): void {
    this._liveClock += dt;
    if (this._liveClock < 2) return;
    this._liveClock = 0;
    const live = this.catalog.slotAtTime(Date.now());
    if (!live || live.slotIndex === this.liveSlot) return;
    this.liveSlot = live.slotIndex;
    this.liveStartMs = live.startsAt;
    if (this.liveSlot - this.windowBase > this.N - 1) {
      const wasAbs = this.windowBase + this.cur;
      this.windowBase = Math.max(0, this.liveSlot - (this.N - 1));
      this.buildSteps();
      // вернуть обложки (уже загруженные)
      for (const s of this.steps) {
        const tex = this.catalog.coverTex[s.trackId];
        if (tex) {
          s.coverMat.map = tex;
          s.coverMat.needsUpdate = true;
        }
      }
      this.cur = clamp(wasAbs - this.windowBase, 0, this.liveIndex);
    }
    if (this.following) this.cur = this.liveIndex;
    this.emit();
  }

  private buildRipples(): void {
    for (let k = 0; k < 5; k++) {
      const mat = new MeshStandardMaterial({
        color: 0x000000,
        emissive: 0x7c6ff2,
        emissiveIntensity: 3,
        roughness: 1,
        transparent: true,
        opacity: 0,
        side: DoubleSide,
        depthWrite: false,
      });
      const m = new Mesh(new RingGeometry(0.94, 1.0, 64), mat);
      m.rotation.x = -Math.PI / 2;
      m.visible = false;
      this.scene.add(m);
      this.ripples.push({ m, mat, active: false, t: 0, dur: 1.4, r0: 1.6 });
    }
  }

  private buildCharacter(): void {
    this.koko = makeKokopelli(this.maxAniso);
    this.char = new Mesh(
      new PlaneGeometry(this.charH, this.charH),
      new MeshBasicMaterial({ map: this.koko.dance[0], transparent: true, alphaTest: 0.5, depthWrite: true }),
    );
    this.scene.add(this.char);

    const sc = document.createElement('canvas');
    sc.width = sc.height = 128;
    const sx = sc.getContext('2d')!;
    const rg = sx.createRadialGradient(64, 64, 4, 64, 64, 60);
    rg.addColorStop(0, 'rgba(38,38,42,0.5)');
    rg.addColorStop(1, 'rgba(38,38,42,0)');
    sx.fillStyle = rg;
    sx.beginPath();
    sx.arc(64, 64, 60, 0, 7);
    sx.fill();
    const blobTex = new CanvasTexture(sc);
    blobTex.colorSpace = SRGBColorSpace;
    this.blob = new Mesh(
      new PlaneGeometry(1.7, 1.7),
      new MeshBasicMaterial({ map: blobTex, transparent: true, depthWrite: false }),
    );
    this.blob.rotation.x = -Math.PI / 2;
    this.scene.add(this.blob);
  }

  /** Мягкий одиночный «пуф» — кирпичик объёмного облака. */
  private puffTex(): Texture {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const x = c.getContext('2d')!;
    const g = x.createRadialGradient(64, 64, 4, 64, 64, 62);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.45, 'rgba(255,255,255,0.88)');
    g.addColorStop(0.8, 'rgba(255,255,255,0.28)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = g;
    x.beginPath();
    x.arc(64, 64, 62, 0, 7);
    x.fill();
    const t = new CanvasTexture(c);
    t.colorSpace = SRGBColorSpace;
    return t;
  }

  private buildClouds(topY: number): void {
    // объёмное облако = кластер мягких пуф-биллбордов в эллипсоиде; низ затенён
    const puff = this.puffTex();
    const n = this.mob ? 9 : 16;
    const basePuffs = this.mob ? 4 : 6;
    for (let i = 0; i < n; i++) {
      const r = rng(100 + i * 13);
      const g = new Group();
      const w = 14 + r() * 26; // габарит облака
      const np = basePuffs + Math.floor(r() * 3);
      for (let k = 0; k < np; k++) {
        const a = r() * Math.PI * 2;
        const rr = Math.pow(r(), 0.6);
        const ox = Math.cos(a) * rr * w * 0.6;
        const oz = Math.sin(a) * rr * w * 0.32;
        const oy = (r() - 0.5) * w * 0.26;
        const ps = w * (0.5 + r() * 0.55);
        const low = oy < -w * 0.03; // нижние пуфы — теневые (объём)
        const tint = low ? 0.74 + r() * 0.1 : 1.0;
        const mat = new SpriteMaterial({
          map: puff,
          transparent: true,
          opacity: (low ? 0.5 : 0.72) + r() * 0.22,
          depthWrite: false,
          fog: true,
          color: new Color(tint, tint, Math.min(1, tint * 1.03)),
        });
        const sp = new Sprite(mat);
        sp.scale.set(ps, ps, 1);
        sp.position.set(ox, oy, oz);
        g.add(sp);
      }
      const ang = r() * Math.PI * 2;
      const rad = 24 + r() * 44;
      g.position.set(Math.cos(ang) * rad, 6 + r() * (topY + 24), Math.sin(ang) * rad);
      this.scene.add(g);
      this.clouds.push({ s: g, sp: 0.18 + r() * 0.5 });
    }
    // тёплое свечение солнца
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const gx = c.getContext('2d')!;
    const g = gx.createRadialGradient(64, 64, 2, 64, 64, 64);
    g.addColorStop(0, 'rgba(255,250,236,0.95)');
    g.addColorStop(0.3, 'rgba(255,243,214,0.5)');
    g.addColorStop(1, 'rgba(255,240,210,0)');
    gx.fillStyle = g;
    gx.fillRect(0, 0, 128, 128);
    const glowTex = new CanvasTexture(c);
    glowTex.colorSpace = SRGBColorSpace;
    const glow = new Sprite(
      new SpriteMaterial({ map: glowTex, transparent: true, blending: AdditiveBlending, depthWrite: false, depthTest: false, opacity: 0.9 }),
    );
    glow.position.copy(this.sun).multiplyScalar(700);
    glow.scale.setScalar(190);
    this.scene.add(glow);
  }

  // ---------- helpers ----------
  private stepTop(i: number, out: Vector3): Vector3 {
    const g = this.steps[i].group.position;
    return out.set(g.x, g.y + this.steps[i].topLocalY + 0.03, g.z);
  }

  private nearestAngle(cur: number, target: number): number {
    let t = target;
    while (t - cur > Math.PI) t -= Math.PI * 2;
    while (t - cur < -Math.PI) t += Math.PI * 2;
    return t;
  }

  // ---------- public API (для UI) ----------
  onState(cb: (s: SceneState) => void): void {
    this.stateCb = cb;
    this.emit();
  }
  private emit(): void {
    const absSlot = this.windowBase + this.cur;
    this.stateCb?.({
      absSlot,
      trackId: this.catalog.trackIdAtSlot(absSlot),
      isLive: this.cur === this.liveIndex,
      liveSlot: this.liveSlot,
      liveStartMs: this.liveStartMs,
      durSec: this.catalog.durSecAtSlot(absSlot),
      playing: this.playing,
    });
    this.applyAutoWeather(); // погода следует за треком (в авто-режиме)
  }
  get index(): number {
    return this.cur;
  }
  get count(): number {
    return this.N;
  }
  private get liveIndex(): number {
    return this.liveSlot - this.windowBase;
  }
  /** Окно эфира для списка: live первым, затем прошлое вниз. */
  windowSlots(): { platform: number; trackId: number; isLive: boolean }[] {
    const out: { platform: number; trackId: number; isLive: boolean }[] = [];
    for (let i = this.liveIndex; i >= 0; i--) {
      out.push({
        platform: i,
        trackId: this.catalog.trackIdAtSlot(this.windowBase + i),
        isLive: i === this.liveIndex,
      });
    }
    return out;
  }
  togglePlay(): void {
    this.setPlaying(!this.playing);
  }
  setPlaying(p: boolean): void {
    if (this.playing === p) return;
    this.playing = p;
    this.emit();
  }
  /** Мгновенный переход на платформу (для списка), без hop-анимации. */
  jumpTo(i: number): void {
    i = clamp(Math.round(i), 0, this.liveIndex);
    this.hop = null;
    this.cur = i;
    this.following = this.cur === this.liveIndex;
    this.emit();
  }

  /** Вернуться в эфир (на live-ступеньку). */
  goLive(): void {
    this.following = true;
    if (this.cur !== this.liveIndex) this.hopTo(this.liveIndex);
    else this.emit();
  }
  hopStep(dir: number): void {
    if (this.hop) {
      this.pendingDir = dir;
      return;
    }
    // навигация только вниз/до эфира: нельзя выше live-слота (будущее не отыграло)
    this.hopTo(clamp(this.cur + dir, 0, this.liveIndex));
  }
  hopTo(target: number): void {
    target = clamp(Math.round(target), 0, this.liveIndex);
    if (target === this.cur || this.hop) return;
    const dist = Math.abs(target - this.cur);
    this.hop = {
      from: this.cur,
      to: target,
      start: performance.now(),
      dur: clamp(340 + dist * 42, 340, 1000),
      height: 1.1 + Math.min(dist, 6) * 0.26,
    };
    this.azTarget = this.nearestAngle(this.azTarget, target * this.angleStep + 0.7);
    this._lastInput = performance.now();
  }
  private finishHop(): void {
    const to = this.hop!.to;
    this.hop = null;
    this.cur = to;
    this.following = this.cur === this.liveIndex;
    this.emit();
    if (this.pendingDir) {
      const d = this.pendingDir;
      this.pendingDir = 0;
      setTimeout(() => this.hopStep(d), 40);
    }
  }

  // ---------- input ----------
  private bindInput(): void {
    const dom = this.renderer.domElement;
    const ptrs = new Map<number, { x: number; y: number }>();
    let moved = 0;
    let pinchStart = 0;
    let distStart = 0;
    dom.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        this._lastInput = performance.now();
        if (e.ctrlKey || e.metaKey) {
          this.distTarget = clamp(this.distTarget + e.deltaY * 0.03, 6, 30);
        } else {
          // одна ступенька за жест: игнор во время прыжка + кулдаун (гасит инерцию)
          const t = performance.now();
          if (this.hop || t - this._lastWheelStep < 220) return;
          this._lastWheelStep = t;
          this.hopStep(e.deltaY > 0 ? -1 : 1);
        }
      },
      { passive: false },
    );
    dom.addEventListener('pointerdown', (e) => {
      try {
        dom.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
      moved = 0;
      this._lastInput = performance.now();
      dom.style.cursor = 'grabbing';
      if (ptrs.size === 2) {
        const p = [...ptrs.values()];
        pinchStart = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
        distStart = this.distTarget;
      }
    });
    dom.addEventListener('pointermove', (e) => {
      if (!ptrs.has(e.pointerId)) return;
      const prev = ptrs.get(e.pointerId)!;
      this._lastInput = performance.now();
      if (ptrs.size === 2) {
        ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const p = [...ptrs.values()];
        const d = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
        if (pinchStart) this.distTarget = clamp(distStart * (pinchStart / d), 6, 30);
        return;
      }
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      moved += Math.abs(dx) + Math.abs(dy);
      this.azTarget -= dx * 0.005;
      this.polarTarget = clamp(this.polarTarget - dy * 0.004, 0.82, 1.46);
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    });
    const up = (e: PointerEvent): void => {
      const wasClick = moved < 8 && ptrs.size <= 1;
      ptrs.delete(e.pointerId);
      dom.style.cursor = 'grab';
      pinchStart = 0;
      if (wasClick) this.tryPick(e.clientX, e.clientY);
    };
    dom.addEventListener('pointerup', up);
    dom.addEventListener('pointercancel', (e) => {
      ptrs.delete(e.pointerId);
      pinchStart = 0;
    });
    addEventListener('keydown', (e) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
        this.hopStep(1);
        e.preventDefault();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
        this.hopStep(-1);
        e.preventDefault();
      } else if (e.key === ' ') {
        this.togglePlay();
        e.preventDefault();
      }
    });
  }

  private tryPick(cx: number, cy: number): void {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.ndc.x = ((cx - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((cy - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const hit = this.raycaster.intersectObjects(this.coverMeshes, false)[0];
    if (hit && hit.object.userData.index != null) {
      // по клику — только на ОДНУ ступеньку в сторону выбранной плиты
      const dir = Math.sign((hit.object.userData.index as number) - this.cur);
      if (dir !== 0) this.hopStep(dir);
    }
  }

  onResize = (): void => {
    const W = this.host.clientWidth || innerWidth;
    const H = this.host.clientHeight || innerHeight;
    if (!W || !H) return;
    this.camera.aspect = W / H;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(W, H);
    this.composer.setSize(W, H);
  };

  // ---------- loop ----------
  start(): void {
    this._last = performance.now();
    this.animate();
  }

  dispose(): void {
    cancelAnimationFrame(this._raf);
    removeEventListener('resize', this.onResize);
    this.renderer.dispose();
  }

  // ---------- погода ----------
  private weatherForTrack(trackId: number): WeatherMode {
    const modes: WeatherMode[] = ['sun', 'wind', 'night', 'storm', 'acid'];
    return modes[((trackId % modes.length) + modes.length) % modes.length];
  }
  private applyAutoWeather(): void {
    if (!this.weatherAuto) return;
    const tid = this.catalog.trackIdAtSlot(this.windowBase + this.cur);
    if (tid === this._weatherTrackId) return;
    this._weatherTrackId = tid;
    this.setWeather(this.weatherForTrack(tid));
  }
  setWeather(mode: WeatherMode): void {
    this.weatherMode = mode;
    const w = WEATHER[mode];
    const u = this.skyMat.uniforms;
    (u.top.value as Color).setHex(w.skyTop);
    (u.bot.value as Color).setHex(w.skyBot);
    (u.sunC.value as Color).setHex(w.sunC);
    const fog = this.scene.fog as Fog | null;
    if (fog) {
      fog.color.setHex(w.fog[0]);
      fog.near = w.fog[1];
      fog.far = w.fog[2];
    }
    (this.scene.background as Color).setHex(w.skyBot);
    // множители дев-панели поверх погодных значений
    this.hemiLight.intensity = w.hemi * this._lightMul;
    this.dirLight.intensity = w.dir * this._lightMul;
    this.dirLight.color.setHex(w.dirColor);
    this.bloom.intensity = w.bloom * this._bloomMul;
    const su = this.hue.uniforms.get('saturation');
    if (su) su.value = w.saturation * this._satMul;
    const cu = this.bc.uniforms.get('contrast');
    if (cu) cu.value = w.contrast;
    this.particles.setMode(w.particles);
    this.windX = w.wind[0];
    this.windZ = w.wind[1];
    this.particles.setWind(this.windX, this.windZ);
    if (mode !== 'storm') this.lightningLight.intensity = 0;
  }

  // ---------- дев-панель: рантайм-регуляторы ----------
  private _lightMul = 1;
  private _bloomMul = 1;
  private _satMul = 1;
  /** Плотность частиц (0..1.5). */
  setParticleDensity(m: number): void {
    this.particles.setDensity(m);
  }
  /** Частота «сказочных» пролётов (0.2..3). */
  setFlybyFrequency(m: number): void {
    this.flybys.setFrequency(m);
  }
  /** Вкл/выкл гигантские обложки-пролёты. */
  setCoversEnabled(on: boolean): void {
    this.flybys.setCoversEnabled(on);
  }
  /** Яркость света (0.2..2.5), множитель поверх погоды. */
  setLightGain(m: number): void {
    this._lightMul = clamp(m, 0.2, 2.5);
    const w = WEATHER[this.weatherMode];
    this.hemiLight.intensity = w.hemi * this._lightMul;
    this.dirLight.intensity = w.dir * this._lightMul;
  }
  /** Сила свечения/bloom (0..3). */
  setBloom(m: number): void {
    this._bloomMul = clamp(m, 0, 3);
    this.bloom.intensity = WEATHER[this.weatherMode].bloom * this._bloomMul;
  }
  /** Насыщенность цвета (0..2). */
  setSaturation(m: number): void {
    this._satMul = clamp(m, 0, 2);
    const su = this.hue.uniforms.get('saturation');
    if (su) su.value = WEATHER[this.weatherMode].saturation * this._satMul;
  }
  /** Цикл: авто → sun → storm → wind → night → acid → авто. Возвращает метку. */
  cycleWeather(): string {
    const order: (WeatherMode | 'auto')[] = ['auto', 'sun', 'storm', 'wind', 'night', 'acid'];
    const cur: WeatherMode | 'auto' = this.weatherAuto ? 'auto' : this.weatherMode;
    const next = order[(order.indexOf(cur) + 1) % order.length];
    if (next === 'auto') {
      this.weatherAuto = true;
      this._weatherTrackId = -1;
      this.applyAutoWeather();
    } else {
      this.weatherAuto = false;
      this.setWeather(next);
    }
    return this.weatherLabel();
  }
  weatherLabel(): string {
    return this.weatherAuto ? 'авто' : this.weatherMode;
  }
  /** Текущий активный режим погоды (для иконки в шапке). */
  weatherKey(): WeatherMode {
    return this.weatherMode;
  }

  private animate = (): void => {
    this._raf = requestAnimationFrame(this.animate);
    const now = performance.now();
    const dt = Math.min(0.05, (now - this._last) / 1000);
    this._last = now;
    this.updateLive(dt); // продвижение эфира (троттлинг внутри)

    // beat-реактив: FFT текущего трека → пульс ступеней/кометы (ресурсы браузера)
    const sig = this.audio ? this.audio.signals() : null;
    const bass = sig ? sig.bass : 0;
    const beat = sig ? sig.beat : 0;
    const level = sig ? sig.level : 0;

    // стержень: импульсы бегут вверх ОТ играющей ступеньки (uStartY ставится ниже, по baseY)
    this.spineMat.uniforms.uTime.value = now * 0.001;
    this.spineMat.uniforms.uBeat.value = beat;
    this.spineMat.uniforms.uBass.value = bass;

    // погода: частицы + молния (для грозы)
    this.particles.update(dt, beat);
    this.flybys.update(dt, now); // пролёты НЛО/дирижаблей/птиц/динозавров
    this.lightningLight.intensity *= 0.9;
    if (this.weatherMode === 'storm') {
      this._lightClock += dt;
      if (this._lightClock > 4) {
        this._lightClock = -Math.random() * 3;
        this.lightningLight.intensity = 500 + Math.random() * 400;
      }
    }

    if (now - this._lastInput > 3000) this.azTarget += 0.0019;
    for (const cl of this.clouds) {
      cl.s.position.x += (cl.sp + this.windX * 0.4) * dt;
      if (cl.s.position.x > 82) cl.s.position.x -= 164;
    }
    this.az += (this.azTarget - this.az) * 0.08;
    this.polar += (this.polarTarget - this.polar) * 0.09;
    this.dist += (this.distTarget - this.dist) * 0.08;

    let baseY: number;
    let arcN = 0;
    let scaleY = 1;
    let scaleX = 1;
    let jumping = false;
    if (this.hop) {
      jumping = true;
      const t = clamp((now - this.hop.start) / this.hop.dur, 0, 1);
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      this.stepTop(this.hop.from, this._t0);
      this.stepTop(this.hop.to, this._t1);
      this._cp.lerpVectors(this._t0, this._t1, e);
      arcN = Math.sin(Math.PI * t);
      baseY = this._cp.y;
      this._cp.y += arcN * this.hop.height;
      scaleY = 1 + 0.18 * arcN;
      scaleX = 1 - 0.12 * arcN;
      if (t >= 1) this.finishHop();
    } else {
      this.stepTop(this.cur, this._cp);
      baseY = this._cp.y;
      const bob = this.playing ? Math.abs(Math.sin(now * 0.00225)) * 0.1 + beat * 0.3 : 0;
      this._cp.y += bob;
    }
    // стержень даёт импульсы вверх от высоты играющей ступеньки (едет за человечком)
    this.spineMat.uniforms.uStartY.value = clamp((baseY - this._spineY0) / this._spineH, 0, 0.95);
    this.char.position.set(this._cp.x, this._cp.y + this.charFeet, this._cp.z);
    const cb = jumping ? 1 : 1 + beat * 0.2 + level * 0.05; // пульс маскота на удар
    this.char.scale.set(this.charH * scaleX * cb, this.charH * scaleY * cb, 1);
    this.char.rotation.y = Math.atan2(this.camera.position.x - this._cp.x, this.camera.position.z - this._cp.z);
    this.blob.position.set(this._cp.x, baseY + 0.06, this._cp.z);
    const bs = 1 - 0.45 * arcN;
    this.blob.scale.set(bs, bs, 1);
    (this.blob.material as MeshBasicMaterial).opacity = 0.5 * (1 - 0.55 * arcN);

    const key = jumping ? 'j' : this.playing ? 'd' + (Math.floor(now / 210) % 8) : 'd0';
    if (key !== this._frameKey) {
      this._frameKey = key;
      (this.char.material as MeshBasicMaterial).map = jumping ? this.koko.jump : this.koko.dance[parseInt(key.slice(1))];
      (this.char.material as MeshBasicMaterial).needsUpdate = true;
    }

    // Ритм-динамика ТОЛЬКО на играющей ступеньке (где человечек). Остальные — покой.
    const breathe = this.playing && !this.hop ? 1 + 0.03 * Math.sin(now * 0.006) + bass * 0.12 : 1;
    for (let i = 0; i < this.steps.length; i++) {
      const s = this.steps[i];
      const on = i === this.cur && !this.hop;
      const emT = on ? 3.2 + beat * 6.5 + bass * 1.8 : s.baseEm;
      const em = s.borderMat.emissiveIntensity;
      // активная: быстрый attack на удар; прочие: плавно вернуть к покою
      s.borderMat.emissiveIntensity = em + (emT - em) * (on && emT > em ? 0.7 : 0.1);
      s.borderMat.emissive.setHex(on ? 0xffcd07 : 0x685ae9);
      const sg = s.group.scale;
      const tg = on ? breathe + beat * 0.24 : 1;
      sg.setScalar(sg.x + (tg - sg.x) * (on ? 0.3 : 0.12));
      // обложка играющей ступеньки «дышит» размером в ритм: квадратик → во всю плиту
      const cov = this.coverMeshes[i];
      if (cov) {
        // активная: квадратик (~55% плиты) → на удар заполняет всю поверхность плиты,
        // но НЕ вылезает за края (потолок = coverMaxScale, вписанный квадрат). Покой = cs.
        const ck = on ? s.coverMaxScale * (0.55 + 0.45 * beat) : 1;
        cov.scale.set(cov.scale.x + (ck - cov.scale.x) * 0.35, cov.scale.y + (ck - cov.scale.y) * 0.35, 1);
      }
    }
    if (this.playing && !this.hop) {
      this._rippleClock += dt;
      // ripple на удар (с мин-кулдауном); без анализатора — фикс-таймер
      const beatTrig = sig ? beat > 0.6 && this._rippleClock > 0.22 : this._rippleClock > 0.78;
      if (beatTrig) {
        this._rippleClock = 0;
        const rp = this.ripples[this._rippleIdx % this.ripples.length];
        this._rippleIdx++;
        this.stepTop(this.cur, this._t1);
        rp.m.position.copy(this._t1);
        rp.m.position.y -= 0.05;
        rp.active = true;
        rp.t = 0;
        rp.r0 = this.steps[this.cur].blobR;
        rp.m.visible = true;
        rp.mat.emissive.setHex(this._rippleIdx % 2 ? 0xffcd07 : 0x7c6ff2);
      }
    }
    for (const rp of this.ripples) {
      if (!rp.active) continue;
      rp.t += dt;
      const k = rp.t / rp.dur;
      if (k >= 1) {
        rp.active = false;
        rp.m.visible = false;
        continue;
      }
      const sc = rp.r0 * (1 + k * 1.7);
      rp.m.scale.set(sc, sc, sc);
      rp.mat.opacity = (1 - k) * 0.5;
    }

    this.focus.lerp(this._t1.set(this._cp.x, this._cp.y + 0.6, this._cp.z), this.hop ? 0.12 : 0.07);
    const sp = Math.sin(this.polar);
    const cp = Math.cos(this.polar);
    this.camera.position.set(
      this.focus.x + this.dist * sp * Math.cos(this.az),
      this.focus.y + this.dist * cp,
      this.focus.z + this.dist * sp * Math.sin(this.az),
    );
    this.camera.lookAt(this.focus);

    this.composer.render();
  };
}
